import { TournamentStatus, TournamentType, TournamentInfoDto } from '@repo/common-types';
import { ButtonGroup, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import {
  FaArchive,
  FaPause,
  FaPlay,
  FaRegEye,
  FaLock,
  FaRedo,
  FaHandPaper,
  FaBell,
  FaUsers,
  FaFlagCheckered,
} from 'react-icons/fa';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { MdCancel, MdPersonRemove } from 'react-icons/md';
import { useNavigate, useMatch } from 'react-router-dom';
import ROUTES from '@/routes/routes-enums';
import { useCallback, useState } from 'react';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';
import { toast } from 'react-toastify';
import {
  extractFundsRest,
  fundPrizePoolRest,
  movePlayerRest,
  closeRegistrationRest,
  eliminatePlayerRest,
  syncPlayerRest,
  adminUnregisterPlayerRest,
  completeTournamentRest,
} from '@/services/tournaments/tournaments-service';
import { FaMoneyBillTrendUp } from 'react-icons/fa6';
import FundPrizePoolModal from './FundPrizePoolModal';
import { TbExchange } from 'react-icons/tb';
import MovePlayerModal from './MovePlayerModal';
import EliminatePlayerModal from './EliminatePlayerModal';
import SyncPlayerModal from './SyncPlayerModal';
import { PiHandWithdrawBold } from 'react-icons/pi';
import { useDispatch } from 'react-redux';
import { cancelTournament } from '@/redux/slices/tournaments';
import usePauseTournament from '@/hooks/usePauseTournament';
import useResumeTournament from '@/hooks/useResumeTournament';
import useUpdateTournament from '@/hooks/useUpdateTournament';
import useRestartTournament from '@/hooks/useRestartTournament';
import useHandByHandMode from '@/hooks/useHandByHandMode';
import { useQueryClient } from '@tanstack/react-query';
import { SettingType } from '@repo/common-types';
import { useTournamentSettings, useUpdateTournamentSetting } from '@/services/settings/useSettingsQueries';
import { useFormStatusReducer } from '@/hooks/useFormStatusReducer';
import TournamentAlertsModal from './TournamentAlertsModal';
import EditSngModalForm from '../SnGs/components/EditSngModalForm';
import EditTournamentModalForm from '../Tournaments/components/EditTournamentModalForm';
import UnregisterPlayerModal from './UnregisterPlayerModal';

interface TournamentQuickActionsProps {
  tournament: TournamentInfoDto & {
    handByHand?: boolean;
  };
}

interface ConfirmationModalData {
  id?: string;
  title?: string;
  message?: string;
  type?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  playerId?: string;
  tableId?: string;
}

const TournamentQuickActions = ({ tournament }: TournamentQuickActionsProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isTournamentDetailsPage = useMatch(`${ROUTES.TOURNAMENT_DETAILS}/${tournament.id}`);

  const { pauseTournament } = usePauseTournament(tournament.id, tournament.url || '');
  const { resumeTournament } = useResumeTournament(tournament.id, tournament.url || '');
  const { updateTournament } = useUpdateTournament(tournament.id);
  const { restartTournament, isRestartingTournament } = useRestartTournament(tournament.id);
  const { enableHandByHand, disableHandByHand, isEnablingHandByHand, isDisablingHandByHand } = useHandByHandMode(
    tournament.id,
    tournament.url || '',
  );

  const [confirmModalData, setConfirmModalData] = useState<ConfirmationModalData | null>(null);
  const [confirmMovePlayerModal, setConfirmMovePlayerModal] = useState<ConfirmationModalData | null>(null);
  const [confirmEliminatePlayerModal, setConfirmEliminatePlayerModal] = useState<ConfirmationModalData | null>(null);
  const [confirmSyncPlayerModal, setConfirmSyncPlayerModal] = useState<ConfirmationModalData | null>(null);
  const [confirmUnregisterPlayerModal, setConfirmUnregisterPlayerModal] = useState<ConfirmationModalData | null>(null);

  const [showFundPrizePoolModal, setShowFundPrizePoolModal] = useState(false);
  const [showMovePlayerModal, setShowMovePlayerModal] = useState(false);
  const [showEliminatePlayerModal, setShowEliminatePlayerModal] = useState(false);
  const [showUnregisterPlayerModal, setShowUnregisterPlayerModal] = useState(false);
  const [showSyncPlayerModal, setShowSyncPlayerModal] = useState(false);
  const [showExtractFundsModal, setShowExtractFundsModal] = useState(false);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formStatus, dispatchFormStatus] = useFormStatusReducer();

  // Payouts pause setting (defaults to true = payouts enabled)
  const { data: settings } = useTournamentSettings(tournament.id, true);
  const { mutate: updateSetting } = useUpdateTournamentSetting();
  const payoutsPaused = settings?.find((s) => s.key === 'payoutsPaused')?.value === 'true' || false;
  const togglePayoutsPause = () => {
    updateSetting({
      tournamentId: tournament.id,
      key: 'payoutsPaused',
      data: { value: (!payoutsPaused).toString(), type: SettingType.BOOLEAN },
    });
  };

  const promptConfirmation = (data: ConfirmationModalData) => {
    setConfirmModalData(data);
  };

  const handleArchiveTournament = useCallback(async () => {
    await updateTournament(
      {
        id: confirmModalData?.id,
        deletedAt: new Date(),
      },
      {
        onSuccess: () => {
          toast.success('Tournament archived successfully');
          setConfirmModalData(null);
        },
      },
    );
  }, [confirmModalData]);

  const handleCancelTournament = useCallback(() => {
    const satelliteCount = tournament.satelliteTournaments?.length ?? 0;

    dispatch(
      cancelTournament({
        tournamentId: tournament.id,
        tournamentUrl: tournament.url || '',
        meta: {
          toast: {
            success: 'Tournament cancelled successfully',
            error: 'Failed to cancel tournament',
          },
          confirm: {
            title: 'Cancel Tournament',
            subtitle: `Are you sure you want to cancel ${tournament.name}? ${satelliteCount > 0 ? `${satelliteCount} linked satellites will also be cancelled!` : ''}`,
            confirmButton: {
              text: 'Yes, cancel this tournament',
              variant: 'danger',
            },
            cancelButton: {
              text: "No, don't cancel this tournament",
              variant: 'secondary',
            },
          },
        },
      }),
    );
  }, [dispatch, tournament.id, tournament.url, tournament.satelliteTournaments]);

  const handleConfirmAction = () => {
    if (confirmModalData?.type === 'archive') {
      handleArchiveTournament();
      return;
    }

    if (confirmModalData?.type === 'pause') {
      handlePauseTournament();
      return;
    }

    if (confirmModalData?.type === 'resume') {
      handleResumeTournament();
      return;
    }

    if (confirmModalData?.type === 'restart') {
      handleRestartTournament();
      return;
    }

    if (confirmModalData?.type === 'close-registration') {
      handleCloseRegistration();
      return;
    }

    if (confirmModalData?.type === 'enable-hand-by-hand') {
      handleEnableHandByHand();
      return;
    }

    if (confirmModalData?.type === 'disable-hand-by-hand') {
      handleDisableHandByHand();
      return;
    }

    if (confirmModalData?.type === 'complete') {
      handleCompleteTournament();
      return;
    }
  };

  const handleFundPrizePool = async (amount: number) => {
    try {
      await fundPrizePoolRest(tournament.id, amount);
      toast.success('Prize pool successfully queued for funding');
      setShowFundPrizePoolModal(false);
    } catch (error) {
      toast.error('Failed to fund prize pool');
    }
  };

  const handleExtractFunds = async (amount: number) => {
    try {
      await extractFundsRest(tournament.id, amount);
      toast.success('Funds successfully queued for extraction');
      setShowExtractFundsModal(false);
    } catch (error) {
      toast.error('Failed to extract funds');
    }
  };

  const handleCloseRegistration = async () => {
    try {
      if (!tournament.url) {
        toast.error('Tournament server not available for this tournament');
        return;
      }

      await closeRegistrationRest(tournament.url);
      toast.success('Registration successfully queued for closing');
      setConfirmModalData(null);
    } catch (error) {
      toast.error('Failed to close registration');
    }
  };

  const handleCompleteTournament = async () => {
    try {
      await completeTournamentRest(tournament.id);
      toast.success('Tournament marked as completed');
      setConfirmModalData(null);
      queryClient.invalidateQueries({ queryKey: ['tournaments'] });
    } catch (error) {
      toast.error('Failed to complete tournament');
    }
  };

  const handleMovePlayer = async (playerId: string, tableId: string) => {
    try {
      await movePlayerRest(tournament.url || '', playerId, tableId);
      toast.success('Player moved successfully');
      setConfirmMovePlayerModal(null);
    } catch (error) {
      toast.error('Failed to move player');
    }
  };

  const handleEliminatePlayer = async (playerId: string) => {
    try {
      await eliminatePlayerRest(tournament.url || '', playerId);
      toast.success('Player eliminated successfully');
      setConfirmEliminatePlayerModal(null);
    } catch (error) {
      toast.error('Failed to eliminate player');
    }
  };

  const handleUnregisterPlayer = async (userId: string) => {
    try {
      await adminUnregisterPlayerRest(tournament.id, userId);
      toast.success('Player unregistered successfully');
      setConfirmUnregisterPlayerModal(null);
    } catch (error) {
      toast.error('Failed to unregister player');
    }
  };

  const handleSyncPlayer = async (playerId: string) => {
    try {
      const result = await syncPlayerRest(tournament.url || '', playerId);
      toast.success(result.message || 'Player synced successfully');
      setConfirmSyncPlayerModal(null);
    } catch (error) {
      toast.error('Failed to sync player');
    }
  };

  const handlePauseTournament = async () => {
    try {
      await pauseTournament(undefined, {
        onSuccess: () => {
          toast.success('Tournament queued for pause');
          setConfirmModalData(null);
        },
        onError: () => {
          toast.error('Failed to pause tournament');
          return false;
        },
      });
    } catch (error) {
      toast.error('Failed to pause tournament');
    }
  };

  const handleResumeTournament = async () => {
    try {
      await resumeTournament(undefined, {
        onSuccess: () => {
          toast.success('Tournament queued for resume');
          setConfirmModalData(null);
        },
        onError: () => {
          toast.error('Failed to resume tournament');
          return false;
        },
      });
    } catch (error) {
      toast.error('Failed to resume tournament');
    }
  };

  const handleRestartTournament = async () => {
    try {
      await restartTournament(undefined, {
        onSuccess: () => {
          toast.success('Tournament restart initiated');
          setConfirmModalData(null);
        },
        onError: () => {
          toast.error('Failed to restart tournament');
          return false;
        },
      });
    } catch (error) {
      toast.error('Failed to restart tournament');
    }
  };

  const handleEnableHandByHand = async () => {
    try {
      await enableHandByHand(undefined, {
        onSuccess: () => {
          toast.success('Hand-by-hand mode enabled');
          setConfirmModalData(null);
        },
        onError: () => {
          toast.error('Failed to enable hand-by-hand mode');
          return false;
        },
      });
    } catch (error) {
      toast.error('Failed to enable hand-by-hand mode');
    }
  };

  const handleDisableHandByHand = async () => {
    try {
      await disableHandByHand(undefined, {
        onSuccess: () => {
          toast.success('Hand-by-hand mode disabled');
          setConfirmModalData(null);
        },
        onError: () => {
          toast.error('Failed to disable hand-by-hand mode');
          return false;
        },
      });
    } catch (error) {
      toast.error('Failed to disable hand-by-hand mode');
    }
  };

  const promptArchive = () => {
    promptConfirmation({
      id: tournament.id,
      title: 'Archive Tournament',
      type: 'archive',
      message: 'Are you sure you want to archive this tournament? This action cannot be undone.',
    });
  };

  const promptPause = () => {
    promptConfirmation({
      id: tournament.id,
      title: 'Pause Tournament',
      type: 'pause',
      message: 'Are you sure you want to pause this tournament?',
      cancelButtonText: "No, don't pause.",
      confirmButtonText: 'Yes, pause this tournament.',
    });
  };

  const promptResume = () => {
    promptConfirmation({
      id: tournament.id,
      title: 'Resume Tournament',
      type: 'resume',
      message: 'Are you sure you want to resume this tournament?',
      cancelButtonText: "No, don't resume.",
      confirmButtonText: 'Yes, resume this tournament.',
    });
  };

  const promptCloseRegistration = () => {
    promptConfirmation({
      id: tournament.id,
      title: 'Close Registration',
      type: 'close-registration',
      message: 'Are you sure you want to close registration for this tournament? This action cannot be undone.',
      cancelButtonText: 'No, keep registration open',
      confirmButtonText: 'Yes, close registration',
    });
  };

  const promptRestart = () => {
    promptConfirmation({
      id: tournament.id,
      title: 'Restart Tournament Server',
      type: 'restart',
      message: 'Are you sure you want to restart the tournament server? This will restart the tournament server.',
      cancelButtonText: 'No, keep server running',
      confirmButtonText: 'Yes, restart server',
    });
  };

  const promptEnableHandByHand = () => {
    promptConfirmation({
      id: tournament.id,
      title: 'Enable Hand-by-Hand Mode',
      type: 'enable-hand-by-hand',
      message:
        'Are you sure you want to enable hand-by-hand mode? All tables will wait for each other to complete hands.',
      cancelButtonText: 'No, keep normal play',
      confirmButtonText: 'Yes, enable hand-by-hand',
    });
  };

  const promptDisableHandByHand = () => {
    promptConfirmation({
      id: tournament.id,
      title: 'Disable Hand-by-Hand Mode',
      type: 'disable-hand-by-hand',
      message: 'Are you sure you want to disable hand-by-hand mode? Tables will resume independent play.',
      cancelButtonText: 'No, keep hand-by-hand',
      confirmButtonText: 'Yes, disable hand-by-hand',
    });
  };

  const promptComplete = () => {
    promptConfirmation({
      id: tournament.id,
      title: 'Complete Tournament',
      type: 'complete',
      message:
        'Are you sure you want to mark this tournament as completed? This will stop the tournament server and finalize the tournament.',
      cancelButtonText: 'No, keep tournament running',
      confirmButtonText: 'Yes, complete tournament',
    });
  };

  const stoppedState: TournamentStatus[] = [TournamentStatus.CANCELLED, TournamentStatus.COMPLETED];
  const runningState: TournamentStatus[] = [TournamentStatus.RUNNING, TournamentStatus.BREAK];

  const showViewButton = !isTournamentDetailsPage;
  const showCancelButton = !tournament.deletedAt && !stoppedState.includes(tournament.status);
  const showArchiveButton = !tournament.deletedAt && stoppedState.includes(tournament.status);

  const showPauseButton = runningState.includes(tournament.status);
  const showResumeButton = tournament.status === TournamentStatus.PAUSED;

  // Show close registration button only while the tournament is running on the tournament server
  const showCloseRegistrationButton =
    !tournament.deletedAt && runningState.includes(tournament.status) && Boolean(tournament.url);

  // Show hand-by-hand buttons only for running tournaments with multiple tables
  const showHandByHandButtons = runningState.includes(tournament.status) && !tournament.deletedAt;

  const emptyMenu = !showViewButton && !showCancelButton && !showArchiveButton;

  if (emptyMenu) {
    return null;
  }

  return (
    <>
      {confirmModalData?.id && (
        <ConfirmModal
          onClose={() => setConfirmModalData(null)}
          onConfirm={handleConfirmAction}
          title={confirmModalData.title || ''}
          message={confirmModalData.message || ''}
          confirmButtonText={confirmModalData.confirmButtonText}
          cancelButtonText={confirmModalData.cancelButtonText}
        />
      )}
      {confirmMovePlayerModal?.id && (
        <ConfirmModal
          onClose={() => setConfirmMovePlayerModal(null)}
          onConfirm={() =>
            confirmMovePlayerModal?.playerId &&
            confirmMovePlayerModal?.tableId &&
            handleMovePlayer(confirmMovePlayerModal?.playerId, confirmMovePlayerModal?.tableId)
          }
          title="Move Player"
          message="Are you sure you want to move this player?"
        />
      )}
      {confirmEliminatePlayerModal?.id && (
        <ConfirmModal
          onClose={() => setConfirmEliminatePlayerModal(null)}
          onConfirm={() =>
            confirmEliminatePlayerModal?.playerId && handleEliminatePlayer(confirmEliminatePlayerModal?.playerId)
          }
          title="Eliminate Player"
          message="⚠️ This will permanently eliminate the player from the tournament. This action cannot be undone. Are you sure you want to eliminate this player?"
          confirmButtonText="Yes, Eliminate Player"
          cancelButtonText="Cancel"
        />
      )}
      {confirmSyncPlayerModal?.id && (
        <ConfirmModal
          onClose={() => setConfirmSyncPlayerModal(null)}
          onConfirm={() => confirmSyncPlayerModal?.playerId && handleSyncPlayer(confirmSyncPlayerModal?.playerId)}
          title="Sync Player"
          message="This will synchronize the player's data from the database to the tournament cache and leaderboard. If the player has no stack, they will be automatically eliminated. Are you sure you want to sync this player?"
          confirmButtonText="Yes, Sync Player"
          cancelButtonText="Cancel"
        />
      )}
      {confirmUnregisterPlayerModal?.id && (
        <ConfirmModal
          onClose={() => setConfirmUnregisterPlayerModal(null)}
          onConfirm={() =>
            confirmUnregisterPlayerModal?.playerId && handleUnregisterPlayer(confirmUnregisterPlayerModal?.playerId)
          }
          title="Unregister Player"
          message="Are you sure you want to unregister this player?"
          confirmButtonText="Yes, Unregister Player"
          cancelButtonText="Cancel"
        />
      )}
      {showFundPrizePoolModal && (
        <FundPrizePoolModal
          onSubmit={handleFundPrizePool}
          onClose={() => setShowFundPrizePoolModal(false)}
          title="Fund Prize Pool"
          description="Fund this tournament's prize pool using the backend wallet"
          submitText="Fund"
        />
      )}
      {showExtractFundsModal && (
        <FundPrizePoolModal
          onSubmit={handleExtractFunds}
          onClose={() => setShowExtractFundsModal(false)}
          title="Extract Funds"
          description="Extract funds from this tournament's prize pool"
          submitText="Extract"
        />
      )}
      {showMovePlayerModal && (
        <MovePlayerModal
          onSubmit={(playerId, tableId) =>
            setConfirmMovePlayerModal({
              id: tournament.id,
              playerId,
              tableId,
            })
          }
          onClose={() => setShowMovePlayerModal(false)}
          title="Move Player"
          description="Move a player from one table to another"
          submitText="Move"
          tournamentId={tournament.id}
        />
      )}
      {showEliminatePlayerModal && (
        <EliminatePlayerModal
          onSubmit={(playerId) =>
            setConfirmEliminatePlayerModal({
              id: tournament.id,
              playerId,
            })
          }
          onClose={() => setShowEliminatePlayerModal(false)}
          title="Eliminate Player"
          tournamentId={tournament.id}
        />
      )}
      {showUnregisterPlayerModal && (
        <UnregisterPlayerModal
          onSubmit={(playerId) =>
            setConfirmUnregisterPlayerModal({
              id: tournament.id,
              playerId,
            })
          }
          onClose={() => setShowUnregisterPlayerModal(false)}
          title="Unregister Player"
          tournamentId={tournament.id}
        />
      )}
      {showSyncPlayerModal && (
        <SyncPlayerModal
          onSubmit={(playerId) =>
            setConfirmSyncPlayerModal({
              id: tournament.id,
              playerId,
            })
          }
          onClose={() => setShowSyncPlayerModal(false)}
          title="Sync Player"
          tournamentId={tournament.id}
        />
      )}
      {showAlertsModal && (
        <TournamentAlertsModal
          tournamentId={tournament.id}
          isOpen={showAlertsModal}
          onClose={() => setShowAlertsModal(false)}
        />
      )}
      {showEditModal &&
        (tournament.tournamentType === TournamentType.SNG ? (
          <EditSngModalForm
            selectedTournament={tournament}
            onClose={() => setShowEditModal(false)}
            onUpdate={async (data) => {
              await updateTournament(data, {
                onSuccess: () => {
                  toast.success('Tournament updated successfully');
                  dispatchFormStatus({ type: 'SUCCESS' });
                  setShowEditModal(false);
                },
                onError: () => {
                  toast.error('Failed to update tournament');
                  dispatchFormStatus({ type: 'ERROR', error: 'Failed to update tournament' });
                },
              });
            }}
            formStatus={formStatus}
          />
        ) : (
          <EditTournamentModalForm
            selectedTournament={tournament}
            onClose={() => setShowEditModal(false)}
            onUpdate={async (data) => {
              await updateTournament(data, {
                onSuccess: () => {
                  toast.success('Tournament updated successfully');
                  dispatchFormStatus({ type: 'SUCCESS' });
                  setShowEditModal(false);
                },
                onError: () => {
                  toast.error('Failed to update tournament');
                  dispatchFormStatus({ type: 'ERROR', error: 'Failed to update tournament' });
                },
              });
            }}
            formStatus={formStatus}
          />
        ))}
      <ButtonGroup>
        <UncontrolledDropdown className="absolute">
          <DropdownToggle tag="button" className="btn btn-soft-primary z-99 d-flex gap-2">
            <p className="mb-0">Actions</p>
            <i className="mdi mdi-chevron-down"></i>
          </DropdownToggle>
          <DropdownMenu className="z-3 dropdownmenu-primary absolute">
            {showPauseButton && (
              <DropdownItem
                className="d-flex justify-content-start align-items-center flex-row gap-2"
                onClick={promptPause}
              >
                <FaPause className="text-muted" size={16} />
                Pause
              </DropdownItem>
            )}
            {showResumeButton && (
              <DropdownItem
                className="d-flex justify-content-start align-items-center flex-row gap-2"
                onClick={promptResume}
              >
                <FaPlay className="text-muted" size={16} />
                Resume
              </DropdownItem>
            )}
            <DropdownItem
              className="d-flex justify-content-start align-items-center flex-row gap-2"
              onClick={promptRestart}
              disabled={isRestartingTournament}
            >
              <FaRedo className="text-muted" size={16} />
              {isRestartingTournament ? 'Restarting...' : 'Restart Server'}
            </DropdownItem>
            {showViewButton && (
              <DropdownItem
                className="d-flex justify-content-start align-items-center flex-row gap-2"
                onClick={() => {
                  navigate(`${ROUTES.TOURNAMENT_DETAILS}/${tournament.id}`);
                }}
              >
                <FaRegEye className="text-muted" size={16} />
                View
              </DropdownItem>
            )}
            {isTournamentDetailsPage && (
              <DropdownItem
                className="d-flex justify-content-start align-items-center flex-row gap-2"
                onClick={() => setShowAlertsModal(true)}
              >
                <FaBell className="text-muted" size={16} />
                Alerts
              </DropdownItem>
            )}
            {isTournamentDetailsPage && (
              <DropdownItem
                className="d-flex justify-content-start align-items-center flex-row gap-2"
                onClick={() => setShowEditModal(true)}
              >
                <IoInformationCircleOutline className="text-muted" size={16} />
                Edit
              </DropdownItem>
            )}
            {showCancelButton && (
              <DropdownItem
                className="d-flex justify-content-start align-items-center flex-row gap-2"
                onClick={handleCancelTournament}
              >
                <MdCancel className="text-muted" size={16} />
                Cancel
              </DropdownItem>
            )}
            {showArchiveButton && (
              <DropdownItem onClick={promptArchive} className="d-flex justify-content-start align-items-center gap-2">
                <FaArchive className="text-muted" size={16} />
                Archive Tournament
              </DropdownItem>
            )}
            {isTournamentDetailsPage && (
              <DropdownItem
                className="d-flex justify-content-start align-items-center flex-row gap-2"
                onClick={togglePayoutsPause}
              >
                <FaPause className="text-muted" size={16} />
                {payoutsPaused ? 'Resume Payouts' : 'Pause Payouts'}
              </DropdownItem>
            )}
            <DropdownItem
              className="d-flex justify-content-start align-items-center flex-row gap-2"
              onClick={() => setShowFundPrizePoolModal(true)}
            >
              <FaMoneyBillTrendUp className="text-muted" size={16} />
              Fund Prize Pool
            </DropdownItem>
            <DropdownItem
              className="d-flex justify-content-start align-items-center flex-row gap-2"
              onClick={() => setShowExtractFundsModal(true)}
            >
              <PiHandWithdrawBold className="text-muted" size={16} />
              Extract Funds
            </DropdownItem>
            {showCloseRegistrationButton && (
              <DropdownItem
                className="d-flex justify-content-start align-items-center flex-row gap-2"
                onClick={promptCloseRegistration}
              >
                <FaLock className="text-muted" size={16} />
                Close Registration
              </DropdownItem>
            )}
            {showHandByHandButtons && !tournament.handByHand && (
              <DropdownItem
                className="d-flex justify-content-start align-items-center flex-row gap-2"
                onClick={promptEnableHandByHand}
                disabled={isEnablingHandByHand}
              >
                <FaHandPaper className="text-muted" size={16} />
                {isEnablingHandByHand ? 'Enabling...' : 'Enable Hand-by-Hand'}
              </DropdownItem>
            )}
            {showHandByHandButtons && tournament.handByHand && (
              <DropdownItem
                className="d-flex justify-content-start align-items-center flex-row gap-2"
                onClick={promptDisableHandByHand}
                disabled={isDisablingHandByHand}
              >
                <FaHandPaper className="text-muted" size={16} />
                {isDisablingHandByHand ? 'Disabling...' : 'Disable Hand-by-Hand'}
              </DropdownItem>
            )}
            <DropdownItem
              className="d-flex justify-content-start align-items-center flex-row gap-2"
              onClick={() => setShowMovePlayerModal(true)}
            >
              <TbExchange className="text-muted" size={16} />
              Move Player
            </DropdownItem>
            <DropdownItem
              className="d-flex justify-content-start align-items-center flex-row gap-2"
              onClick={() => setShowEliminatePlayerModal(true)}
            >
              <MdCancel className="text-muted" size={16} />
              Eliminate Player
            </DropdownItem>
            <DropdownItem
              className="d-flex justify-content-start align-items-center flex-row gap-2"
              onClick={() => setShowUnregisterPlayerModal(true)}
            >
              <MdPersonRemove className="text-muted" size={16} />
              Unregister Player
            </DropdownItem>
            <DropdownItem
              className="d-flex justify-content-start align-items-center flex-row gap-2"
              onClick={() => setShowSyncPlayerModal(true)}
            >
              <FaRedo className="text-muted" size={16} />
              Sync Player
            </DropdownItem>
            {isTournamentDetailsPage && (
              <DropdownItem
                className="d-flex justify-content-start align-items-center flex-row gap-2"
                onClick={() => navigate(`${ROUTES.TOURNAMENT_REGISTRATIONS}/${tournament.id}`)}
              >
                <FaUsers className="text-muted" size={16} />
                Registrations
              </DropdownItem>
            )}
            <DropdownItem
              className="d-flex justify-content-start align-items-center flex-row gap-2"
              onClick={promptComplete}
            >
              <FaFlagCheckered className="text-muted" size={16} />
              Complete Tournament
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </ButtonGroup>
    </>
  );
};

export default TournamentQuickActions;
